import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger } from '@core';
import { AuthDataService } from '@app/auth/auth-data.service';
import { AuthQuery } from '@app/auth/auth.query';

import { MatSnackBar } from '@angular/material/snack-bar';

const log = new Logger('ChangePassword');

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit {
  version: string | null = environment.version;
  error: string | undefined;
  loginForm!: UntypedFormGroup;
  isLoading = false;

  // Email verification
  emailVerified = false;
  isAdmin = false;
  userEmail = '';
  verificationCode: string[] = ['', '', '', '', '', ''];
  verificationError = '';
  attemptsRemaining = 5;
  isSendingCode = false;
  isVerifying = false;
  @ViewChild('codeInput0') codeInput0!: ElementRef<HTMLInputElement>;
  @ViewChild('codeInput1') codeInput1!: ElementRef<HTMLInputElement>;
  @ViewChild('codeInput2') codeInput2!: ElementRef<HTMLInputElement>;
  @ViewChild('codeInput3') codeInput3!: ElementRef<HTMLInputElement>;
  @ViewChild('codeInput4') codeInput4!: ElementRef<HTMLInputElement>;
  @ViewChild('codeInput5') codeInput5!: ElementRef<HTMLInputElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthDataService,
    private authQuery: AuthQuery,
    private _snackBar: MatSnackBar
  ) {
    this.createForm();
  }

  ngOnInit() {
    // Check query params for activation token (for first-time users)
    let hasActivationToken = false;
    this.route.queryParams.subscribe(params => {
      hasActivationToken = !!(params['token'] && params['account_id']);
    }).unsubscribe();
    
    // If user is not logged in but has activation token, allow them to proceed
    // Otherwise, require login
    if (!this.authQuery.isLoggedIn && !hasActivationToken) {
      this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    // Check if user is kontakt (role_id 12) - only if logged in
    if (this.authQuery.isLoggedIn) {
      const userRole = this.authQuery.role;
      if (userRole !== 12) {
        this.router.navigate(['/reports'], { replaceUrl: true });
        return;
      }

      // Check if firststart_done is already 1, then redirect to reports
      if (this.authQuery.firststart_done === 1) {
        this.router.navigate(['/reports'], { replaceUrl: true });
        return;
      }
    }

    // Monitoring app only allows kontakt users (role 12), so they are never admins
    // Admins (role 1 or 2) would use the webapp, not the monitoring app
    this.isAdmin = false;
    this.userEmail = this.authQuery.email || '';

    // Check query params for verification status and activation token
    this.route.queryParams.subscribe(params => {
      const verified = params['verified'];
      const activationToken = params['token'];
      const accountId = params['account_id'];
      
      // If user is not logged in but has activation token, we can still proceed
      // The sendVerificationCode will use the activation token
      if (verified === 'false' && !this.isAdmin) {
        // Auto-send verification code on component init
        // This will work even if not logged in, as long as activation token is in query params
        this.sendVerificationCode();
      } else if (this.isAdmin) {
        // Admin: skip verification
        this.emailVerified = true;
      }
      
      // If not logged in but have activation token, we need to get email from account
      // For now, the sendVerificationCode will handle it with the activation token
    });
  }

  ngOnDestroy() {}

  logout() {
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/login'], { replaceUrl: true });
    });
  }

  sendVerificationCode() {
    this.isSendingCode = true;
    this.verificationError = '';

    // Check if we have activation token and account_id in route params (for first-time users)
    let activationToken: string | undefined;
    let accountId: number | undefined;
    
    this.route.queryParams.subscribe(params => {
      activationToken = params['token'];
      const accountIdParam = params['account_id'];
      if (accountIdParam) {
        accountId = parseInt(accountIdParam, 10);
      }
    }).unsubscribe(); // Unsubscribe immediately after getting values

    this.authService.sendEmailVerification(activationToken, accountId).subscribe({
      next: (response: any) => {
        if (response.success) {
          this._snackBar.open('Ein Verifizierungscode wurde an Ihre E-Mail-Adresse gesendet.', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        } else {
          this.verificationError = response.error || 'Fehler beim Senden des Codes';
        }
        this.isSendingCode = false;
      },
      error: (error: any) => {
        this.verificationError = error.error?.error || 'Fehler beim Senden des Codes';
        this.isSendingCode = false;
      }
    });
  }

  onCodeInput(index: number, event: any) {
    const value = event.target.value.replace(/[^0-9]/g, ''); // Only allow digits
    this.verificationCode[index] = value.slice(0, 1); // Only take first character

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = this.getCodeInput(index + 1);
      if (nextInput) {
        setTimeout(() => nextInput.nativeElement.focus(), 0);
      }
    }

    // Auto-submit when all 6 digits are entered
    if (this.isCodeComplete()) {
      setTimeout(() => this.verifyCode(), 300);
    }
  }

  onCodeKeyDown(index: number, event: KeyboardEvent) {
    // Handle backspace
    if (event.key === 'Backspace' && !this.verificationCode[index] && index > 0) {
      const prevInput = this.getCodeInput(index - 1);
      if (prevInput) {
        prevInput.nativeElement.focus();
      }
    }
  }

  getCodeInput(index: number): ElementRef<HTMLInputElement> | null {
    switch (index) {
      case 0: return this.codeInput0;
      case 1: return this.codeInput1;
      case 2: return this.codeInput2;
      case 3: return this.codeInput3;
      case 4: return this.codeInput4;
      case 5: return this.codeInput5;
      default: return null;
    }
  }

  isCodeComplete(): boolean {
    return this.verificationCode.every(code => code.length === 1);
  }

  verifyCode() {
    if (!this.isCodeComplete()) {
      return;
    }

    this.isVerifying = true;
    this.verificationError = '';

    const code = this.verificationCode.join('');

    // Check if we have activation token and account_id in route params (for first-time users)
    let activationToken: string | undefined;
    let accountId: number | undefined;
    
    this.route.queryParams.subscribe(params => {
      activationToken = params['token'];
      const accountIdParam = params['account_id'];
      if (accountIdParam) {
        accountId = parseInt(accountIdParam, 10);
      }
    }).unsubscribe(); // Unsubscribe immediately after getting values

    this.authService.verifyEmailCode(code, activationToken, accountId).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.emailVerified = true;
          this.verificationError = '';
          this._snackBar.open('E-Mail erfolgreich verifiziert', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 2000,
          });
        } else {
          this.verificationError = response.message || 'Ungültiger Verifizierungscode';
          
          // Update attempts remaining from response (0 is a valid value, so check for undefined/null)
          if (response.attempts_remaining !== undefined && response.attempts_remaining !== null) {
            this.attemptsRemaining = response.attempts_remaining;
          } else {
            // Fallback: decrement if backend doesn't provide the value
            this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);
          }
          
          // Clear code inputs on error
          this.verificationCode = ['', '', '', '', '', ''];
          const firstInput = this.getCodeInput(0);
          if (firstInput) {
            setTimeout(() => firstInput.nativeElement.focus(), 0);
          }
        }
        this.isVerifying = false;
      },
      error: (error: any) => {
        // Check if this is a verification code error (400 with message)
        if (error.error && error.error.message) {
          this.verificationError = error.error.message;
          
          // Update attempts remaining from error response if available
          if (error.error.attempts_remaining !== undefined && error.error.attempts_remaining !== null) {
            this.attemptsRemaining = error.error.attempts_remaining;
          } else {
            // Fallback: decrement if backend doesn't provide the value
            this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);
          }
          
          // Show specific error message for incorrect code
          if (error.error.message.includes('Ungültiger Verifizierungscode') || error.error.message.includes('nicht korrekt')) {
            this._snackBar.open('Der Code ist nicht korrekt', 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          } else {
            // Show the error message from backend
            this._snackBar.open(error.error.message, 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          }
          
          // Clear code inputs on error
          this.verificationCode = ['', '', '', '', '', ''];
          const firstInput = this.getCodeInput(0);
          if (firstInput) {
            setTimeout(() => firstInput.nativeElement.focus(), 0);
          }
        } else {
          this.verificationError = error.error?.error || 'Fehler bei der Verifizierung';
          // Fallback: decrement attempts
          this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);
        }
        this.isVerifying = false;
      }
    });
  }

  changePassword() {
    if (this.loginForm.invalid) {
      return;
    }

    // For non-admin users, check if email is verified
    if (!this.isAdmin && !this.emailVerified) {
      this._snackBar.open('Bitte verifizieren Sie zuerst Ihre E-Mail-Adresse', 'Schließen', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 3000,
      });
      return;
    }

    const password = this.loginForm.controls['password'].value;
    const passwordConfirm = this.loginForm.controls['pwdconfirm'].value;

    // Validate passwords match
    if (password !== passwordConfirm) {
      this._snackBar.open('Die Passwörter stimmen nicht überein', 'Schließen', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 3000,
      });
      return;
    }

    // Validate password length
    if (password.length < 6) {
      this._snackBar.open('Bitte geben Sie ein längeres Kennwort ein (mindestens 6 Zeichen)', 'Schließen', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 3000,
      });
      return;
    }

    this.isLoading = true;

    // Get user email and access token from auth query
    const email = this.authQuery.email || '';
    const accessToken = this.authQuery.access_token || '';

    let data = {
      email: email,
      token: accessToken,
      uid: this.authQuery.userid || 0,
      password: password,
      password_confirm: passwordConfirm,
    };

    const changePwd$ = this.authService.changepwd(data);
    changePwd$
      .pipe(
        finalize(() => {
          this.loginForm.markAsPristine();
          this.isLoading = false;
        })
      )
      .subscribe(
        (response) => {
          // Check if password change was successful
          if (response.code == 200) {
            this._snackBar.open('Kennwortänderung erfolgreich durchgeführt', 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 2000,
            });

            // Update firststart_done to 1 in auth store and persist it
            this.authService.updateFirststart({ firststart_done: 1 });

            // Verify the value was updated immediately
            const firststartDone = this.authQuery.firststart_done;
            console.log('Firststart done after update:', firststartDone);
            
            // Use window.location for a hard redirect to ensure guard sees updated value
            // This forces a full page reload and ensures the guard checks the persisted value
            setTimeout(() => {
              window.location.href = '/reports';
            }, 500);
          } else if (response.requires_email_verification) {
            this._snackBar.open('Bitte verifizieren Sie zuerst Ihre E-Mail-Adresse', 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          } else {
            this._snackBar.open('Fehler aufgetreten: ' + (response.message || 'Unbekannter Fehler'), 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          }
        },
        (error) => {
          // show error notification alert
          this._snackBar.open('Fehler aufgetreten: ' + (error.message || 'Unbekannter Fehler'), 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      );
  }

  private createForm() {
    this.loginForm = this.formBuilder.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      pwdconfirm: ['', [Validators.required, Validators.minLength(6)]],
    });
  }
}
